<template>
   <v-container fluid class="pa-4">
      <v-row dense>
        <v-col cols="6" sm="12">
            <v-card class="pa-4 mb-4 rounded-lg" elevation="2">
                <v-row dense justify="space-between">
                    <v-col cols="4" sm="4">
                        <v-date-input
                            v-model="filters.from_date"
                            label="From Date"
                            density="compact"
                            variant="outlined"
                            prepend-icon=""
                            prepend-inner-icon="mdi mdi-calendar-month"
                            single-line
                            bg-color="grey-lighten-4"
                            autocomplete="off"
                            hide-details
                        ></v-date-input>
                    </v-col>
                    <v-col cols="4" sm="4">
                        <v-date-input
                            v-model="filters.to_date"
                            label="To Date"
                            density="compact"
                            variant="outlined"
                            prepend-icon=""
                            prepend-inner-icon="mdi mdi-calendar-month"
                            single-line
                            bg-color="grey-lighten-4"
                            autocomplete="off"
                            hide-details
                        ></v-date-input>
                    </v-col>
                    <v-col cols="2" sm="2" class="d-flex align-center">
                        <v-btn color="primary" block @click="loadDashboard">
                            <v-icon left>mdi-magnify</v-icon> Filter
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card>
        </v-col>
      </v-row>

    <!-- KPI Cards -->
    <v-row class="mb-6" dense>
    

      <v-col cols="12" md="3" v-for="card in kpiCards" :key="card.title">
        <v-card class="pa-4 rounded-lg d-flex bg-gray" elevation="2" style="min-height: 113px;">
          <v-row align="center" justify="space-between">
            <v-col>
              <v-icon color="primary">{{ card.icon }}</v-icon>
              <div class="text-subtitle-2 text-grey-darken-1">{{ card.title }}</div>
            </v-col>
            <v-col class="text-right">
              <v-chip color="success">
                  {{ card.value }}
              </v-chip>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- PO Trend Chart -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card class="pa-4" elevation="2">
          <div class="text-h6 mb-2">Purchase Order Trend</div>
          <apexchart
            type="line"
            height="350"
            :options="poTrendOptions"
            :series="poTrendSeries"
          ></apexchart>
        </v-card>
      </v-col>
    </v-row>

    <!-- Top Suppliers Table -->
    <v-row>
      <v-col cols="12">
        <v-card elevation="2">
          <v-card-title>Top Suppliers by Evaluation Score</v-card-title>
          <v-data-table
            :headers="topSuppliersHeaders"
            :items="topSuppliers"
            density="compact"
            class="elevation-1"
            hide-default-footer
          >
            <template #item.avg_score="{ item }">
              <v-progress-linear
                :value="item.avg_score"
                color="primary"
                height="10"
                rounded
              ></v-progress-linear>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from "vue";
import axios from "axios";
import ApexCharts from "vue3-apexcharts";
import formattedDate from "@/plugins/dateFormatter";

const baseURL = import.meta.env.VITE_API_BASE_URL || "";

const filters = ref({
  from_date: "",
  to_date: "",
});


const summary = ref({});
const kpiCards = ref([]);
const poTrendSeries = ref([]);
const poTrendOptions = ref({});
const topSuppliers = ref([]);
const topSuppliersHeaders = [
  { text: "Supplier", value: "name" },
  { text: "Average Score", value: "avg_score" },
  { text: "Times Evaluated", value: "times_evaluated" },
];

// ----------------------------
// Fetch Data
// ----------------------------

const loadDashboard = async () => {
  filters.value.from_date = formattedDate(filters.value.from_date);
  filters.value.to_date = formattedDate(filters.value.to_date);
  fetchKPIs();
  fetchPOTrend();
  fetchTopSuppliers();
};

const fetchKPIs = async () => {
  try {
    const res = await axios.get(`${baseURL}/auth/purchase/dashboard/summary`, { params: filters.value });
    summary.value = res.data.data;

    if (res.data.success) {
      kpiCards.value = [
        { title: "Total Requisitions", value: summary.value.total_requisitions, icon: "mdi-file-document-edit-outline" },
        { title: "Total RFQs", value: summary.value.total_rfqs, icon: "mdi-format-quote-open-outline" },
        { title: "Total CS Quotations", value: summary.value.total_cs_quotations, icon: "mdi-file-document-outline" },
        { title: "Total POs", value: summary.value.total_pos, icon: "mdi-shopping-outline" },
        { title: "PO Value", value: summary.value.total_po_value, icon: "mdi-cash-multiple" },
        { title: "Invoice Value", value: summary.value.total_invoice_value, icon: "mdi-invoice-outline" },
        { title: "Avg Delivery Time (days)", value: summary.value.avg_delivery_time_days, icon: "mdi-truck-delivery-outline" },
        { title: "Suppliers Participated", value: summary.value.suppliers_participated, icon: "mdi-account-group-outline" },
      ];
    }
  } catch (error) {
    console.error("Error fetching KPI summary:", error);
  }
};

const fetchPOTrend = async () => {
  try {
    // filters.value.from_date = formattedDate(filters.value.from_date);
    // filters.value.to_date = formattedDate(filters.value.to_date);
    // filters.value.group_by = 1

    const res = await axios.get(`${baseURL}/auth/purchase/dashboard/po-trend`, { params: filters.value });
    const po_trend = res.data.data;

    if (res.data.success) {
      poTrendSeries.value = [
        {
          name: "PO Value",
          data: po_trend.map((item) => item.total_value),
        },
        {
          name: "PO Count",
          data: po_trend.map((item) => item.pos),
        },
      ];

      poTrendOptions.value = {
        chart: { id: "po-trend" },
        xaxis: {
          categories: po_trend.map((item) => item.period),
          title: { text: "Period" },
        },
        yaxis: [
          { title: { text: "PO Value" }, seriesName: "PO Value" },
          { opposite: true, title: { text: "PO Count" }, seriesName: "PO Count" },
        ],
        tooltip: { shared: true, intersect: false },
      };
    }
  } catch (error) {
    console.error("Error fetching PO Trend:", error);
  }
};

const fetchTopSuppliers = async () => {
  try {
    // filters.value.from_date = formattedDate(filters.value.from_date);
    // filters.value.to_date = formattedDate(filters.value.to_date);
    const res = await axios.get(`${baseURL}/auth/purchase/dashboard/top-suppliers`, { params: filters.value });
    const top_supplier = res.data.data;
    if (res.data.success) {
      topSuppliers.value = top_supplier;
    }
  } catch (error) {
    console.error("Error fetching Top Suppliers:", error);
  }
};

onMounted(() => {
  loadDashboard();
  // fetchKPIs();
  // fetchPOTrend();
  // fetchTopSuppliers();
});
</script>

<style scoped>
.v-card {
  border-radius: 12px;
}

.v-data-table .v-progress-linear {
  margin-top: 4px;
}
</style>
